<template>
  <div>
    <es-form>
      <template #es-form>
        <div class="fields-container" v-if="isAddAuthorization">
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label" for="formAddRoleControl">Secondary Role</label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <div class="select is-full-width"
                      :class="{ 'is-danger' : hasErrorRole}">
                    <select class="input is-full-width"
                      id="formAddRoleControl"
                      v-model="role"
                      @change="getPermissions(role)"
                      aria-label="Secondary Role"
                    >
                    <option value=""></option>
                    <option v-for="(role, index) in sortedRoles"
                      :key="index" :value="role.id">
                      {{ role.name }}
                    </option>
                  </select>
                </div>
              </div>
              <p v-if="hasErrorRole" class="invalid-feedback has-text-left">{{ msgRole}}</p>
              <div class="form-subtext has-text-left">
                Role manages what components and privileges a user can
                have access to within the application.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="role"> 
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">District</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <div class="buttons has-addons">
                    <button v-for="(option, index) in districtOptions" :key="index"
                      :disabled = "option.name === 'All Districts' ? !isStateUser : false"
                      class="button no-radius"
                      :class="{'selected-type': districtType === option.name}"
                      @click="selectedType('districtType', option.name)">
                      {{ option.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="districtType === 'Specific Districts'" class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label" for="formAddDistrictControl">District(s)</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    :class="{'is-danger': hasErrorDistrict }"
                    v-bind:multi="true"
                    v-bind:search="true"
                    api="districts"
                    v-on:update:values="districts = $event"
                    ref="selDistricts"
                    :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                    searchMinLength="2"
                    keyValue="code"
                    controlId="formAddDistrictControl"
                  />
                </div>
                <span v-if="hasErrorDistrict" class="help is-danger">
                  {{ msgDistrict }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">School</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input v-if="districtType === 'All Districts'"
                    class="input is-static"
                    type="text"
                    v-model="schoolType"
                    readonly
                    aria-label="School" />
                  <div class="buttons has-addons"
                    v-if="districtType === 'Specific Districts'">
                    <button
                      v-for="(option, index) in schoolOptions" :key="index"
                      :disabled="(!districts.length) || (districts.length && option.name === 'All Schools' ? userSchools.length !== 0 : false)"
                      class="button no-radius"
                      :class="{'selected-type': schoolType === option.name}"
                      @click="selectedType('schoolType', option.name)">
                        {{ option.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="schoolType === 'Specific Schools'" class="row removeUnderline" >
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label" for="formAddSchoolControl">School(s)</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    :class="{ 'is-danger': hasErrorSchool }"
                    v-bind:multi="true"
                    v-bind:search="true"
                    api="schools"
                    :apiQueryParams="getDistrictParams"
                    v-on:update:values="schools = $event"
                    ref="selSchools"
                    :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                    :subValueTplFn="(result) => `${result.districtLocation.name}
                    (<i>${result.districtCode }</i>)`"
                    searchMinLength="2"
                    keyValue="code"
                    controlId="formAddSchoolControl"
                  />
                </div>
                <span v-if="hasErrorSchool" class="help is-danger">
                  {{ msgSchool }}
                </span>
              </div>
            </div>
          </div>
        </div> 
        <div v-if="showMatchTypes">
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label" for="formAddMatchtypeControl">DirectMatch Match Type(s)</label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <es-select
                      v-bind:multi="true"
                      v-on:update:values="programs = $event;"
                      key-value="code"
                      :setValues="filterProgramsByPersonType"
                      :value-tpl-fn="(result) => `${result.description}`"
                      :preSelected="programs"
                      v-bind:clearOut="true"
                      controlId="formAddMatchtypeControl"
                    />
                  </div>
                  <div class="form-subtext has-text-left">
                    Match Type allows the user to access to specific match
                    types throughout the application.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">Location Group</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <div class="buttons has-addons">
                    <button v-for="(option, index) in locationGroupOptions"
                      :disabled="option.label === 'All Groups' ? !hasAllGroupAccess : false
                        || option.label === 'Specific Groups' ? !hasSpecificGroupAccess : false"
                      :key="index"
                      class="button no-radius"
                      :class="{'selected-type': locationGroupType === option}"
                      @click="selectedType('locationGroupType', option)">
                    {{ option }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="locationGroupType === 'Specific Groups'"  class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label" for="formAddGroupControl">Location Group(s)</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    :class="{ 'is-danger': hasErrorLocationGroup }"
                    v-bind:multi="true"
                    v-bind:search="true"
                    api="location-groups"
                    :apiQueryParams="locationGroupQueryParams"
                    v-on:update:values="locationGroups = $event"
                    ref="selGroups"
                    valueTpl="${label}"
                    searchMinLength="3"
                    keyValue="id"
                    controlId="formAddGroupControl"
                  />
                </div>
                <span v-if="hasErrorLocationGroup" class="help is-danger">
                  {{ msgLocationGroup }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</es-form>
<div class="es-actions" v-if="isAddAuthorization">
  <es-form-actions>
    <template #form-actions>
      <div class="bottom-content">
          <ul>
            <li>
              <div class="list-content">
                <a tabindex="0" role="button"
                  @click.prevent="cancelAdd()"
                  @keypress.enter.prevent="cancelAdd()"
                >
                  Cancel
                </a>
              </div>
            </li>
            <li>
              <div class="list-content">
                <button
                class="button is-primary"
                @click.prevent="addAuthorization()">
                Add Role
                </button>
              </div>
            </li>
          </ul>
          </div>
        </template>
      </es-form-actions>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import EsForm from '@/components/EsForm.vue';
import PermissionManager from '@/mixins/PermissionManager';
import RolePermissions from '@/mixins/RolePermissions';
import EsSelect from '@/components/EsSelect.vue';
import EsFormActions from '@/components/EsFormActions.vue';
import NotificationManager from '@/mixins/NotificationManager';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    PermissionManager,
    NotificationManager,
    RolePermissions,
    FieldLabels,
  ],
  components: {
    EsSelect,
    EsForm,
    EsFormActions,
  },
  props: [
    'isAddAuthorization',
    'roles',
    'userId',
    'isStateUser',
    'filterProgramsByPersonType'
  ],
  data() {
    return {
      apiQueryParams: { active: 'true' },
      locationGroupQueryParams: { status: 'true' },
      districts: [],
      schools: [],
      locationGroups: [],
      programs: [],
      role: null,
      districtOptions: [],
      districtType: null,
      schoolOptions: [],
      schoolType: 'All Schools',
      locationGroupOptions: ['All Groups', 'Specific Groups', 'No Groups' ],
      locationGroupType: 'No Groups',
      hasErrorLocationGroup: false,
      hasErrorRole: false,
      hasErrorDistrict: false,
      hasErrorSchool: false,
      msgWarning: false,
      msgDistrict: '',
      msgSchool: '',
      msgRole: '',
      msgLocationGroup: '',
      formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
    };
  },
  watch: {
    districtType() {
      if (this.districtType === 'All Districts') {
        this.schoolType = `All Schools`;
      }
    },
    districts() {
      if (this.districts.length && this.hasErrorDistrict) {
        this.hasErrorDistrict = false;
        this.hideErrorNotification();
      }
    },
    schools() {
      if (this.schools.length && this.hasErrorSchool) {
        this.hasErrorSchool = false;
        this.hideErrorNotification();
      }
    },
    locationGroups() {
      if (this.locationGroups.length && this.hasErrorLocationGroup) {
        this.hasErrorLocationGroup = false;
        this.hideErrorNotification();
      }
    },
  },
  created() {
    this.districtOptions= [
      {
        name: 'All Districts',
        label: `All Districts`,
      },
      {
        name: 'Specific Districts',
        label: `Specific Districts`,
      },
    ];
    this.schoolOptions= [
      {
        name: 'All Schools',
        label: `All Schools`,
      },
      {
        name: 'Specific Schools',
        label: `Specific Schools`,
      },
    ];
    if (this.isStateUser) {
      this.districtType = 'All Districts';
    } else {
      this.districtType = 'Specific Districts';
    }
    if (!this.userSchools.length) {
      this.schoolType = 'All Schools';
    } else {
      this.schoolType = 'Specific Schools';
    }
    if (this.locationGroupType === 'No Groups') {
      this.locationGroups = [{ id: -1 }];
    }
  },
  computed: {
    ...mapGetters({
      userSchools: 'userSchools',
      hasAllGroupAccess: 'hasAllGroupAccess',
      hasSpecificGroupAccess: 'hasSpecificGroupAccess',
    }),
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(district => district.code);
      return params;
    },
    sortedRoles() {
      return this.roles.slice().sort((a, b) => (a.name > b.name ? 1 : -1));
    },
    hasError() {
      return this.hasErrorDistrict
      || this.hasErrorSchool
      || this.hasErrorLocationGroup
      || this.hasErrorRole;
    },
  },
  methods: {
    getPermissions(role) {
      if (role) {
        this.hasRolePermissions(role);
      } else {
        this.privileges.clear();
      }
    },
    hideErrorNotification() {
      if (!this.hasError) {
        this.hideNotification();
      }
    },
    selectedType(field, option) {
      if (field === 'districtType') {
        if (option === 'All Districts') {
          if (this.schools.length && !this.hasError) {
            this.showWarning('Data has been removed from the School(s) field based on the changes to the District(s) field.');
            this.msgWarning = true;
          }
          this.$refs.selDistricts.clear();
          if (this.schoolType === 'Specific Schools') {
            this.$refs.selSchools.clear();
          }
          this.hasErrorDistrict = false;
          this.msgDistrict = '';
        }
        this.districtType = option;
      }
      if (field === 'schoolType') {
        if (option === 'All Schools') {
          this.$refs.selSchools.clear();
          this.hasErrorSchool = false;
          this.msgSchool = '';
        }
        this.schoolType = option;
      }
      if (field === 'locationGroupType') {
        if (option === 'All Groups') {
          this.$refs.selGroups ? this.$refs.selGroups.clear() : this.locationGroups = [] ;
          this.hasErrorLocationGroup = false;
          this.msgGroup = '';
        }
        else if (option === 'No Groups') {
          this.locationGroups = [{ id: -1 }];
          this.hasErrorLocationGroup= false;
          this.msgLocationGroup= '';
        }
        else if (option === 'Specific Groups') {
          this.locationGroups = this.locationGroups.filter( lg => lg.id !== -1);
        }
        this.locationGroupType = option;
      }
      if (!this.msgWarning && !this.hasError) {
        this.hideNotification();
      }
      this.msgWarning = false;
    },
    async addAuthorization() {
      if (this.formValidation()) {
        this.showError(this.formErrorMsg);
        return;
      }
      this.hideNotification();
      try {
        let districtsList = this.districts.map(val => ({ code: val.code }));
        if (districtsList.length === 0) {
          districtsList = [{ code: 'ALL_LOCATIONS' }];
        }
        let schoolsList = this.schools.map(val => ({ code: val.code, districtCode: val.districtCode }));
        if (schoolsList.length === 0) {
          schoolsList = districtsList.map(val => ({ code: 'ALL_LOCATIONS', districtCode: val.code }));
        }

        const payload = {
          role: { id: this.role },
          roleId: this.role,
          districts: districtsList,
          schools: schoolsList,
          locationGroups: this.locationGroups.map(val => ({ locationGroupId: val.id })),
          programs: this.programs.map(val => ({ programCode: val.code })),
        };
        await axios.post(`/users/${this.userId}/locationpermissions`, (payload));
        this.showSuccess('The role has been added.');
        this.$emit('reloadAuthorizationPanel');
        this.resetForm();
      } catch (error) {
        const { status } = error.response;
        let apiMsg = '';
        if (status === 500) {
          apiMsg = 'Network Error';
        } else if (status === 400) {
          apiMsg = `${error.response.data.message}`;
        } else if (status === 403) {
          apiMsg = 'User Does Not Have Access To This Resource.';
        } else {
          apiMsg = 'Something went wrong.';
        }
        this.showError(apiMsg);
      }
    },
    formValidation() {
      this.resetFormErrors();
      let error = 0;
      if (!this.role) {
        this.hasErrorRole = true;
        this.msgRole = 'Role is requred';
      }
      if (this.role) {
        if (this.districtType === 'Specific Districts') {
          if (!this.districts || !this.districts.length) {
            this.hasErrorDistrict = true;
            this.msgDistrict = 'District is required';
            error += 1;
          }
        }
        if (this.schoolType === 'Specific Schools') {
          if (!this.schools || !this.schools.length) {
            this.hasErrorSchool = true;
            this.msgSchool = 'School is required';
            error += 1;
          }
        }
        if (this.schoolType === 'Specific Schools') {
          if (this.schools.length) {
            const isSchoolSelected = this.districts.map(item => item.code)
              .every(item => this.schools
                .map(item => item.districtCode)
                .includes(item));
            if (!isSchoolSelected) {
              this.hasErrorSchool = true;
              this.msgSchool = 'Please select at least one school for each selected district(s)';
              error += 1;
            }
          }
        }
        if (this.locationGroupType === 'Specific Groups') {
          if (!this.locationGroups || !this.locationGroups.length) {
            this.hasErrorLocationGroup = true;
            this.msgLocationGroup = 'Location Group is required.';
            error += 1;
          }
        }
      }
      return error;
    },
    cancelAdd() {
      this.resetErrors();
      this.$emit('clearAll', 'addAuthorization');
    },
    resetErrors() {
      this.resetForm();
      this.resetFormErrors();
    },
    resetForm() {
      this.role = null;
      this.schools = [];
      this.districts = [];
      this.locationGroups = [{ id: -1 }];
      this.programs = [];
      this.districtType = 'All Districts';
      this.schoolType = 'All Schools';
      this.locationGroupType = 'No Groups';
    },
    resetFormErrors() {
      this.hasErrorRole = false;
      this.hasErrorDistrict = false;
      this.hasErrorSchool = false;
      this.hasErrorLocationGroup = false;
      this.msgRole = '';
      this.msgDistrict = '';
      this.msgSchool = '';
      this.msgLocationGroup = '';
    },
    showError(error) {
      this.showNotification('danger', [error]);
    },
    showSuccess(error) {
      this.showNotification('success', [error]);
    },
    showWarning(error) {
      this.showNotification('warning', [error]);
    },
  },
};

</script>
  <style lang="sass" scoped>
  .selected-type
    background-color: #ccc
    font-weight: bold
  .form-subtext
    color: #757575
    font-style: italic
    font-size: .85rem
  .invalid-feedback
    font-style: italic
    color: #dc3545
    font-size: .8rem
    padding: .25rem 0 .25rem .15rem
  </style>
